import { createSlice } from "@reduxjs/toolkit";
import { backendConnector } from "../backendIntegration/lambdas";
import { createApiThunk, addAsyncCases } from "./sliceHelpers";
import logger from "../utils/logger";

// these notifications should have these data:
/* 
  company: string;
  status: string;
  messageType: string;
  date: string;
  timestamp: string;
  isNotificationRead: boolean;
  isNotificationDeleted: boolean;
*/

// A page request is a bare timestamp (job view) or { bookmarkTimestamp, view }.
const readPageArg = (arg) => {
  const { bookmarkTimestamp = null, view = "job" } =
    arg && typeof arg === "object" ? arg : { bookmarkTimestamp: arg };
  return { bookmarkTimestamp: bookmarkTimestamp ?? null, view };
};

export const fetchFiftyBasicEmailData = createApiThunk(
  "emails/fetchFiftyBasicEmailData",
  (arg) => {
    const { bookmarkTimestamp, view } = readPageArg(arg);
    return backendConnector.getFiftyBasicEmailData(bookmarkTimestamp, view);
  },
  {
    logMessage: "Error fetching 50 basic email data:",
    errorMessage: "Failed to fetch 50 basic email data",
  }
);

/** The first page of job mail, unless it was fetched in the last minute (My Jobs mounts often). */
export const fetchFirstMailPageIfStale = (maxAgeMs = 60_000) => (dispatch, getState) => {
  const at = getState().emails?.recentJobEmailsAt ?? 0;
  if (Date.now() - at < maxAgeMs) return null;
  return dispatch(fetchFiftyBasicEmailData(null));
};

export const fetchAllKeptNotifications = createApiThunk(
  "emails/fetchAllKeptNotifications",
  () => backendConnector.getAllKeptNotifications(),
  {
    logMessage: "Error fetching all kept notifications:",
    errorMessage: "Failed to fetch all kept notifications",
  }
);

export const updateNotificationField = createApiThunk(
  "emails/updateEmailNotification",
  ({ emailId, fieldName }) =>
    backendConnector.updateEmailNotification(emailId, fieldName),
  {
    logMessage: "Error updating the notification data:",
    errorMessage: "failed to update the notification data",
  }
);

export const toggleEmailField = createApiThunk(
  "emails/toggleEmailField",
  ({ emailId, fieldName, value }) =>
    backendConnector.toggleEmailField(emailId, fieldName, value),
  {
    logMessage: "Error toggling the email data:",
    errorMessage: "failed to toggle the notification data",
  }
);

const initialState = {
  keptNotifications: [], // Used for PageHeader notifications display
  basicEmailData: [],
  // The newest page of job mail, kept apart from the page the Mailbox is showing: My Jobs reads it
  // to find the recruiter email behind a card, and paging or opening Promotions must not take that away.
  recentJobEmails: [],
  newEmailsLoaded: false,
  newEmailsLoading: false,
  notificationsLoaded: false,
  notificationsLoading: false,
  fiftyEmailsLoading: false,
  fiftyEmailsLoaded: false,
  // The newest page request. Only its answer may land, so a page that arrives after sign-out (the
  // reset puts this back to null) or after a newer request cannot refill or overwrite the slice.
  pageRequestId: null,
  error: null,
};

export const emailSlice = createSlice({
  name: "emails",
  initialState,
  reducers: {
    // Wipes every cached email/notification back to a signed-out state. Mail is
    // per-user data, so this MUST run on sign-out: the mailbox skips its initial
    // fetch when fiftyEmailsLoaded is already true, so a leftover deck would be
    // shown verbatim to whoever signs in next.
    clearEmailData: () => ({ ...initialState }),

    // ***** keptNotifications Section:
    // REMOVE AND MARK AS READ by setting isNotificationRead and isNotificationDeleted
    // Update a notification field (optimistic update) - sets to true since you can't revert a read or delete
    updateReduxNotificationField: (state, action) => {
      const { emailId, fieldName } = action.payload;
      const notification = state.keptNotifications.find(
        (n) => n.emailId === emailId
      );
      if (notification && notification[fieldName] !== undefined) {
        notification[fieldName] = true;
      }
    },

    // ***** BasicEmailData section
    // Toggles the flagged and read status by using isEmailRead and isEmailFlagged
    toggleReduxEmailField: (state, action) => {
      const { emailId, fieldName } = action.payload;
      const normalizedId = String(emailId).trim();
      const email = state.basicEmailData.find(
        (n) => String(n.emailId).trim() === normalizedId
      );
      if (email && email[fieldName] !== undefined) {
        email[fieldName] = !email[fieldName];
      }
    },
    // A server-collapsed chain row carries the ids of its unread and flagged messages
    // (`threadUnreadIds` / `threadFlaggedIds`). This puts one id in or out of one of those lists, and
    // when the id is the row's own message, its own field follows (read = not unread, flagged = listed).
    setChainMembership: (state, action) => {
      const { rowId, emailId, list, present } = action.payload;
      const row = state.basicEmailData.find((n) => String(n.emailId).trim() === String(rowId).trim());
      if (!row || !Array.isArray(row[list])) return;
      const id = String(emailId).trim();
      const rest = row[list].filter((x) => String(x).trim() !== id);
      row[list] = present ? [...rest, id] : rest;
      if (id === String(row.emailId).trim()) {
        if (list === "threadUnreadIds") row.isEmailRead = !present;
        else if (list === "threadFlaggedIds") row.isEmailFlagged = present;
      }
    },
    // Opening an application's emails reads its received messages on the server: these ids are read
    // here too, in the list and in the chain rows' unread ids.
    markEmailsRead: (state, action) => {
      const ids = new Set((action.payload?.emailIds ?? []).map((id) => String(id).trim()));
      if (ids.size === 0) return;
      [state.basicEmailData, state.recentJobEmails].forEach((list) => {
        (list ?? []).forEach((row) => {
          if (ids.has(String(row.emailId).trim())) row.isEmailRead = true;
          if (Array.isArray(row.threadUnreadIds)) row.threadUnreadIds = row.threadUnreadIds.filter((id) => !ids.has(String(id).trim()));
        });
      });
    },
    // A reply through JobHarvest went out for this message: the row says "Replied" at once,
    // without waiting for the next page fetch to bring `repliedAt` (and `threadId`) back.
    markEmailReplied: (state, action) => {
      const { emailId, repliedAt, threadId } = action.payload;
      const normalizedId = String(emailId).trim();
      const at = repliedAt || new Date().toISOString();
      [state.basicEmailData, state.recentJobEmails].forEach((list) => {
        const email = (list ?? []).find((n) => String(n.emailId).trim() === normalizedId);
        if (!email) return;
        email.repliedAt = at;
        // The send answer names the conversation the reply started, so the row groups at once.
        if (threadId && !email.threadId) email.threadId = threadId;
      });
    },
    // ***** New emails section
  },
  extraReducers: (builder) => {
    // Written by hand rather than with addAsyncCases: the latest request wins, so an answer is
    // applied only when it belongs to the request the slice is waiting on.
    builder
      .addCase(fetchFiftyBasicEmailData.pending, (state, action) => {
        state.pageRequestId = action.meta.requestId;
        state.fiftyEmailsLoading = true;
        state.fiftyEmailsLoaded = false;
        state.error = null;
      })
      .addCase(fetchFiftyBasicEmailData.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.pageRequestId) return;
        state.fiftyEmailsLoading = false;
        state.fiftyEmailsLoaded = true;
        const rawBasicEmails = action.payload?.data?.fiftyBasicEmailData;
        const basicEmailArray = Array.isArray(rawBasicEmails)
          ? rawBasicEmails
          : [];
        state.basicEmailData = basicEmailArray;
        const { bookmarkTimestamp, view } = readPageArg(action.meta?.arg);
        if (bookmarkTimestamp === null && view === "job") {
          state.recentJobEmails = basicEmailArray;
          state.recentJobEmailsAt = Date.now();
        }
      })
      .addCase(fetchFiftyBasicEmailData.rejected, (state, action) => {
        if (action.meta.requestId !== state.pageRequestId) return;
        state.fiftyEmailsLoading = false;
        state.fiftyEmailsLoaded = true;
        state.error = action.payload;
      });
    addAsyncCases(builder, fetchAllKeptNotifications, {
      loadingKey: "notificationsLoading",
      loadedKey: "notificationsLoaded",
      onFulfilled: (state, action) => {
        const rawNotifications =
          action.payload?.data?.allKeptNotifications || [];

        const processedNotifications = rawNotifications.map((notification) => {
          if (!notification.timestamp) {
            notification.timestamp = `${Date.now()}`;
          }
          if (notification.isNotificationRead === undefined) {
            notification.isNotificationRead = false;
          }
          // Ensure message field exists for display
          if (!notification.message && notification.messageType) {
            notification.message = notification.messageType;
          }
          return notification;
        });

        // Store in keptNotifications (sorted by timestamp, newest first)
        const sortedNotifications = processedNotifications.sort((a, b) => {
          const tsA = parseInt(a.timestamp) || 0;
          const tsB = parseInt(b.timestamp) || 0;
          return tsB - tsA; // Descending order (newest first)
        });
        state.keptNotifications = sortedNotifications;
      },
    });
    builder
      .addCase(toggleEmailField.fulfilled, (state, action) => {
        // Backend update successful - state already updated optimistically
        // If needed, you can sync with backend response here
      })
      .addCase(toggleEmailField.rejected, (state, action) => {
        // On error, you might want to revert the optimistic update
        // This would require storing the previous state, or refetching
        logger.error("Failed to toggle email field:", action.payload);
      })
      .addCase(updateNotificationField.fulfilled, (state, action) => {
        // Backend update successful - state already updated optimistically
        // If needed, you can sync with backend response here
      })
      .addCase(updateNotificationField.rejected, (state, action) => {
        // On error, you might want to revert the optimistic update
        logger.error("Failed to update notification fields:", action.payload);
      });
  },
});

export const { clearEmailData, updateReduxNotificationField, toggleReduxEmailField, setChainMembership, markEmailsRead, markEmailReplied } =
  emailSlice.actions;

export default emailSlice.reducer;
